import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { PageTitle } from '@/components'
import { Button, Checkbox, Input, Modal } from '@/components/ui'
import SelectField from '@/components/select-field'
import FormGroup from '@/core/components/FormGroup'
import { HtmlEditor } from '@/core/components/HtmlEditor'
import {
  AVAILABILITY_TYPES,
  AvailabilityType,
  QuestGroupTranslationDto,
  QuestGroupUpsertDto,
  TagDto,
} from '@/common/models/gamification'

import { TranslationTabs } from '../shared/TranslationTabs'
import { TagsField } from './TagsField'

interface QuestGroupEditorModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Current group settings to edit (meta only; quests are managed by the caller). */
  group?: QuestGroupUpsertDto | null
  /** The group's own tags (distinct from its member quests' tags). */
  tags?: TagDto[]
  /** Festival edition id — threaded down to the tags picker. */
  editionId: string
  onSubmit: (
    data: QuestGroupUpsertDto,
    tags: TagDto[],
  ) => Promise<void> | void
}

export const QuestGroupEditorModal = ({
  open,
  onOpenChange,
  group,
  tags: initialTags,
  editionId,
  onSubmit,
}: QuestGroupEditorModalProps) => {
  const isEditMode = !!group

  const [availabilityType, setAvailabilityType] =
    useState<AvailabilityType>('GENERAL')
  const [completeCount, setCompleteCount] = useState<number>(1)
  const [ordinal, setOrdinal] = useState<number>(0)
  const [active, setActive] = useState<boolean>(true)
  const [translations, setTranslations] = useState<QuestGroupTranslationDto[]>(
    [],
  )
  const [tags, setTags] = useState<TagDto[]>([])
  const [saving, setSaving] = useState(false)

  // `HtmlEditor` is built around react-hook-form (`Controller` needs a valid `control` + `name`),
  // but this modal is plain `useState` — the instructions HTML itself still flows entirely through
  // `TranslationTabs`' `value`/`onFieldChange`, exactly like `title`/`subtitle`. This form only
  // exists to hand `HtmlEditor` a `control` object; nothing is ever registered/submitted through it.
  const { control: htmlEditorControl } = useForm()

  useEffect(() => {
    if (!open) return
    if (group) {
      setAvailabilityType(group.availabilityType)
      setCompleteCount(group.completeCount ?? 1)
      setOrdinal(group.ordinal ?? 0)
      setActive(group.active ?? true)
      setTranslations(group.translations ?? [])
      setTags(initialTags ?? [])
    } else {
      setAvailabilityType('GENERAL')
      setCompleteCount(1)
      setOrdinal(0)
      setActive(true)
      setTranslations([])
      setTags([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, group, initialTags])

  const invalid = completeCount < 1

  const handleClose = () => onOpenChange(false)

  const submit = async () => {
    if (invalid) return
    const data: QuestGroupUpsertDto = {
      availabilityType,
      completeCount,
      ordinal,
      active,
      translations,
    }
    try {
      setSaving(true)
      await onSubmit(data, tags)
      handleClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose()
      }}
      size="lg"
      title={
        <div className="border-b border-monochrome-120 pb-4">
          <PageTitle
            icon="events-star"
            title={
              <span className="flex items-center gap-2">
                {isEditMode ? 'Edit' : 'Add New'}{' '}
                <span className="text-accent-1">Quest Group</span>
              </span>
            }
          />
        </div>
      }
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button onClick={handleClose} type="button" variant="secondary">
            Cancel
          </Button>
          <Button
            onClick={submit}
            type="button"
            variant="primary"
            disabled={invalid || saving}
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <SelectField
          label="Availability"
          required
          value={availabilityType}
          onValueChange={(v) => setAvailabilityType(v as AvailabilityType)}
          options={AVAILABILITY_TYPES.map((a) => ({ value: a, label: a }))}
        />

        <FormGroup
          label="Complete count"
          error={invalid ? 'Must be at least 1' : undefined}
        >
          <Input
            type="number"
            min="1"
            value={completeCount}
            isInvalid={invalid}
            onChange={(e) => setCompleteCount(Number(e.target.value))}
          />
        </FormGroup>

        <FormGroup label="Order">
          <Input
            type="number"
            min="0"
            value={ordinal}
            onChange={(e) => setOrdinal(Number(e.target.value))}
          />
        </FormGroup>

        <label className="flex items-center gap-3 cursor-pointer">
          <Checkbox
            checked={active}
            onCheckedChange={(checked) => setActive(!!checked)}
          />
          <span className="text-body-medium text-monochrome-1">Active</span>
        </label>

        <TagsField editionId={editionId} value={tags} onChange={setTags} />

        <div className="border-t border-monochrome-120 pt-6">
          <div className="text-lg font-bold text-monochrome-1 mb-3">
            Translations
          </div>
          <TranslationTabs<QuestGroupTranslationDto>
            value={translations}
            onChange={setTranslations}
            makeEmpty={(locale) => ({
              locale,
              title: '',
              subtitle: '',
              instructions: '',
            })}
            renderFields={({ value, locale, onFieldChange }) => {
              return (
                <div className="space-y-4">
                  <FormGroup label="Title">
                    <Input
                      value={value.title ?? ''}
                      onChange={(e) =>
                        onFieldChange({ title: e.target.value })
                      }
                    />
                  </FormGroup>
                  <FormGroup label="Subtitle">
                    <Input
                      value={value.subtitle ?? ''}
                      onChange={(e) =>
                        onFieldChange({ subtitle: e.target.value })
                      }
                    />
                  </FormGroup>
                  <HtmlEditor
                    // Radix `TabsContent` already unmounts inactive locale tabs by default (no
                    // `forceMount`), so switching locales tears down the previous CKEditor instance
                    // and remounts a fresh one seeded with this locale's `value`. The `key` makes
                    // that remount-per-locale explicit/guaranteed rather than relying on that Radix
                    // default, so each locale's instructions stay independent.
                    key={locale}
                    label="Instructions"
                    // `index` only ever feeds the preview iframe's title attribute, and this modal
                    // never enables `showPreview` — so it is inert here. Passing a constant rather
                    // than fetching the festival's languages again just to compute a value nothing
                    // renders.
                    index={0}
                    name={`quest-group-instructions.${locale}`}
                    variables={[]}
                    control={htmlEditorControl}
                    value={value.instructions ?? ''}
                    onChange={(data) => onFieldChange({ instructions: data })}
                    isError={false}
                  />
                </div>
              )
            }}
          />
        </div>
      </div>
    </Modal>
  )
}
